var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';
import { Flex, Skeleton, Typography } from '@robinpowered/ui-kit';
import { GetMeetingServiceRequestsForEventBySpaceIdDocument, } from '../../../generated/graphql';
import { SpaceAmenities } from './SpaceAmenities';
import { SpaceRequestFormDetails } from '../WorkplaceService/SpaceRequestFormDetails';
import { ServiceRequestDetails } from '../WorkplaceService/ServiceRequestDetails';
import { useWatch } from 'react-hook-form';
import { useFeatureFlags } from '@robinpowered/dashboard-apps-common';
import { useCallback, useMemo } from 'react';
import { SpaceInfoWrapper } from './EventSpaceWrapper';
import { useSpaceDetailsContext } from './SpaceDetailsContext';
import { LocationLevel } from './components/LocationLevel';
/* @TODO: This component is a bit messy. We should refactor it to be more modular. */
export var SpaceDetails = function (_a) {
    var spaceId = _a.spaceId;
    var t = useTranslation('meetingRooms').t;
    /* @TODO Remodify this component to accept an array of spaces */
    var formSpaces = useWatch({
        name: 'spaces',
    });
    var eventUid = useWatch({ name: 'uid' });
    var _b = useSpaceDetailsContext(), setWorkplaceServiceRequestFormStatus = _b.setWorkplaceServiceRequestFormStatus, spaceForMeetingServiceData = _b.spaceForMeetingServiceData, spaceForMeetingServiceLoading = _b.spaceForMeetingServiceLoading, refetchSpaceForMeetingService = _b.refetchSpaceForMeetingService, requestOnlySpace = _b.requestOnlySpace, handleGetSpaceForMeetingService = _b.handleGetSpaceForMeetingService, initialEvent = _b.initialEvent;
    var isSpaceRequestOnlyEvent = !!requestOnlySpace;
    var checkFlag = useFeatureFlags().checkFlag;
    var allowSpaceRequestFormFeatureFlag = checkFlag('hydra(space-request-forms)', false);
    var space = formSpaces === null || formSpaces === void 0 ? void 0 : formSpaces.find(function (space) { return space.id === spaceId; });
    /* @TODO get the service details for the space Id */
    var serviceDetails = spaceForMeetingServiceData === null || spaceForMeetingServiceData === void 0 ? void 0 : spaceForMeetingServiceData.getSpacesByIds[0];
    var apolloClient = useApolloClient();
    var cacheVariables = useMemo(function () {
        var _a;
        return ({
            spaceId: spaceId.toString(),
            eventInstance: {
                robinEventId: undefined,
                ical: {
                    // Keep behavior aligned with `useWorkplaceServiceRequest`, which may
                    // pass `undefined` uid during composer initialization.
                    uid: eventUid,
                    recurrenceId: (_a = initialEvent === null || initialEvent === void 0 ? void 0 : initialEvent.recurrenceId) !== null && _a !== void 0 ? _a : null,
                },
            },
        });
    }, [eventUid, spaceId, initialEvent === null || initialEvent === void 0 ? void 0 : initialEvent.recurrenceId]);
    var onRemoveServiceRequest = useCallback(function (serviceRequestId) {
        // Optimistically update the Apollo cache so the removed request
        // disappears immediately.
        try {
            var existing = apolloClient.readQuery({
                query: GetMeetingServiceRequestsForEventBySpaceIdDocument,
                variables: cacheVariables,
            });
            if (existing) {
                var nextGetSpacesByIds = existing.getSpacesByIds.map(function (space) {
                    var _a, _b;
                    if (space.id !== spaceId.toString())
                        return space;
                    return __assign(__assign({}, space), { meetingServiceRequests: (_b = (_a = space.meetingServiceRequests) === null || _a === void 0 ? void 0 : _a.filter(function (request) { return request.id !== serviceRequestId; })) !== null && _b !== void 0 ? _b : [] });
                });
                apolloClient.writeQuery({
                    query: GetMeetingServiceRequestsForEventBySpaceIdDocument,
                    variables: cacheVariables,
                    data: {
                        __typename: 'Query',
                        getSpacesByIds: nextGetSpacesByIds,
                    },
                });
            }
        }
        catch (_a) {
            /* noop */
        }
        // Ensure server state is reflected (network-only) to handle cases where
        // the Apollo cache doesn't have this query entry yet.
        handleGetSpaceForMeetingService(cacheVariables, 'cache-first');
    }, [apolloClient, cacheVariables, handleGetSpaceForMeetingService, spaceId]);
    var hasAmenities = useMemo(function () {
        return ((space === null || space === void 0 ? void 0 : space.amenities) && space.amenities.length > 0) || (space === null || space === void 0 ? void 0 : space.capacity);
    }, [space]);
    return (_jsxs(_Fragment, { children: [_jsxs(SpaceInfoWrapper, { showBorder: false, style: {
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    display: 'flex',
                }, children: [_jsxs(Flex, { gap: 4, justify: "flex-start", style: {
                            flexDirection: 'column',
                            alignSelf: 'flex-start',
                        }, children: [_jsx(Typography.Title, { level: 5, style: { marginBottom: 0 }, children: space === null || space === void 0 ? void 0 : space.name }), (space === null || space === void 0 ? void 0 : space.locationId) && (space === null || space === void 0 ? void 0 : space.levelId) && (_jsx(LocationLevel, { locationId: space === null || space === void 0 ? void 0 : space.locationId.toString(), levelId: space === null || space === void 0 ? void 0 : space.levelId.toString() })), hasAmenities && (_jsx(SpaceAmenities, { capacity: (space === null || space === void 0 ? void 0 : space.capacity) || undefined, amenities: (space === null || space === void 0 ? void 0 : space.amenities) || [] }))] }), (space === null || space === void 0 ? void 0 : space.image) && (_jsx(SpaceImage, { alt: t('create_event.space_image_alt', {
                            spaceName: space.name,
                        }), title: t('create_event.space_image_alt', {
                            spaceName: space.name,
                        }), src: space.image }))] }), isSpaceRequestOnlyEvent && allowSpaceRequestFormFeatureFlag && (_jsx(SpaceRequestFormDetails, { setWorkplaceServiceRequestFormStatus: setWorkplaceServiceRequestFormStatus, spaceForMeetingServiceData: spaceForMeetingServiceData, refetchSpaceForMeetingService: refetchSpaceForMeetingService })), spaceForMeetingServiceLoading ? (_jsx(SpaceInfoWrapper, { showBorder: true, children: _jsx(ServiceLoading, { children: _jsx(Skeleton, { style: { width: '100%' } }) }) })) : (serviceDetails && (_jsx(ServiceRequestDetails, { serviceDetails: serviceDetails, onEditServiceRequest: function (serviceRequestId) {
                    return setWorkplaceServiceRequestFormStatus({
                        type: 'edit',
                        serviceRequestId: serviceRequestId,
                    });
                }, onRemoveServiceRequest: onRemoveServiceRequest })))] }));
};
var SpaceImage = styled.img(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  border-radius: 4px;\n  width: 40px;\n  height: 40px;\n  object-fit: cover;\n"], ["\n  border-radius: 4px;\n  width: 40px;\n  height: 40px;\n  object-fit: cover;\n"])));
var ServiceLoading = styled.div(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  display: flex;\n  flex-direction: column;\n  gap: 6px;\n"], ["\n  display: flex;\n  flex-direction: column;\n  gap: 6px;\n"])));
var templateObject_1, templateObject_2;
